import { useCallback } from 'react';

import { useCoreSelector } from '../../CoreStore';

import { selectIsStreamEnabled, selectIsStudioChannelEnabled } from './rawSelectors';
import {
  ChannelVolumeCallbackType,
  ChannelVolumeValueCallbackType,
  StreamQuality,
  UiChannelType,
  VolumeChannelType,
} from './types';

export const useSettingsSlice = {
  getIsInFullscreen: (): boolean => {
    return useCoreSelector(({ settings }) => settings.isInFullscreen);
  },

  /* Language */
  currentLanguage: (): string => {
    return useCoreSelector(({ settings }) => settings.language.current);
  },
  getLanguageHandle: (): string => {
    return useCoreSelector(({ settings }) => settings.language.handle);
  },
  getIsCurrentLanguageRTL: (): boolean => {
    return useCoreSelector(({ settings }) => settings.language.isRTL);
  },

  /* Stream */
  getIsStreamEnabled: (): boolean => {
    return useCoreSelector(selectIsStreamEnabled);
  },
  getStreamQualityLimit: (): number => {
    return useCoreSelector(({ settings }) => settings.stream.qualityLimit);
  },
  getStreamQuality: (): StreamQuality => {
    return useCoreSelector(({ settings }) => settings.stream.quality);
  },
  getIsVideoPlaying: (): boolean => {
    return useCoreSelector(({ settings }) => settings.stream.isVideoPlaying);
  },
  /**
   * Raw stream latency value from video player.
   * @returns Updated stream latency in milliseconds
   */
  getStreamLatency: (): number => {
    return useCoreSelector(({ settings }) => settings.stream.streamLatency);
  },
  getIsAnimationEnabled: (): boolean => {
    return useCoreSelector(({ settings }) => settings.isAnimationEnabled);
  },
  getIsSecondBetBoardToggle: (): boolean => {
    return useCoreSelector(({ settings }) => settings.isSecondBetBoardToggle);
  },

  // Chat settings
  getShowOtherMessages: (): boolean => {
    return useCoreSelector(({ settings }) => settings.showOtherMessages);
  },

  /* Volume */
  getNoVolumeControl: (): boolean | undefined => {
    return useCoreSelector(({ settings }) => settings.volume.noVolumeControl);
  },
  getUIVolumeChannel: (): UiChannelType => {
    return useCoreSelector(({ settings }) => settings.volume.uiChannel);
  },
  getVolumeChannels: (): Record<string, VolumeChannelType> => {
    return useCoreSelector(({ settings }) => settings.volume.channels);
  },
  getVolumeChannelsReady: (channels: string[]): boolean => {
    return useCoreSelector(({ settings }) => {
      return channels.every((channel) => {
        return settings.volume.channels[channel] !== undefined;
      });
    });
  },
  getChannelVolumes: (): Record<string, number> => {
    const masterVolume = masterVolumeValue();
    const channels = useCoreSelector(({ settings }) => settings.volume.channels);
    const volumes: Record<string, number> = {};
    for (const channelName in channels) {
      const channelVolume = channels[channelName]?.value || 0;
      volumes[channelName] = masterVolume * channelVolume;
    }
    return volumes;
  },
  getAllIsMutedCheck: (): boolean => {
    let isMuted = true;
    const channels = useCoreSelector(({ settings }) => settings.volume.channels);
    Object.values(channels).forEach((chanel) => {
      if (chanel.value > 0) isMuted = false;
    });
    return isMuted;
  },
  getVolumeChannel: (): ChannelVolumeCallbackType => {
    return useCallback((channelName: string) => channelVolume(channelName), []);
  },
  getVolumeChannelRawValue: (): ChannelVolumeValueCallbackType => {
    return useCallback((channelName: string) => channelVolumeValue(channelName), []);
  },
  getVolumeChannelValue: (channelName: string): number => {
    const masterVolume = masterVolumeValue();
    const channelVolume = channelVolumeValue(channelName);
    return masterVolume * channelVolume;
  },
  getMasterChannel: (): VolumeChannelType => {
    return useCoreSelector(({ settings }) => settings.volume.master);
  },
  getWasVolumeRestored: (): boolean => {
    return useCoreSelector(({ settings }) => settings.volume.restored || false);
  },
  getWasAnySoundChannelEnabled: (): boolean => {
    return useCoreSelector(({ settings }) => {
      const channels = settings.volume.channels;

      return Object.values(channels).some((channel) => channel.previousValue);
    });
  },
  getMasterChannelValue: (): number => masterVolumeValue(),
  getIsStudioSoundDisabled: (): boolean | undefined => {
    return useCoreSelector(({ settings }) => settings.isStudioSoundDisabled);
  },
  getIsStudioChannelEnabled: (): boolean => {
    return useCoreSelector(selectIsStudioChannelEnabled);
  },
  // TODO: Revisit the code to handle game specific tabs from game level
  getSettingsTabToHide: (): string[] => {
    return useCoreSelector(({ settings }) => settings.settingsTabToHide ?? []);
  },
};

const masterVolumeValue = (): number => {
  return useCoreSelector(({ settings }) => {
    if (!settings.isGameVisible) return 0;
    const masterVolume = settings.volume.master;
    return masterVolume.temporaryValue ?? masterVolume.value;
  });
};

const channelVolume = (channelName: string): VolumeChannelType | null => {
  return useCoreSelector(({ settings }) => settings.volume.channels[channelName] ?? null);
};

const channelVolumeValue = (channelName: string): number => {
  const channel = channelVolume(channelName);
  if (channel) {
    return channel.temporaryValue ?? channel.value;
  }
  return 0;
};
